import ErrorPayload from 'src/models/ErrorPayload';

export async function init(context) {
  await context.dispatch('getPlaces');
  context.dispatch('map/updatePlacesLayer', null, { root: true });
  await context.commit('initialized');
}

/**
 * Retrieves Places from the API.
 *
 * @param {ActionContext} context
 * @returns {Place[]}
 */
export async function getPlaces(context) {
  context.commit('incrementPendingRequests');
  const places = await context.rootState.app.broker.getPlaces();

  if (!places.error) {
    context.commit('setPlaces', places);
  }

  context.commit('decrementPendingRequests');
  return context.getters.places;
}

/**
 * Deletes a place via the API.
 *
 * @param {ActionContext} context
 * @param {String} key
 * @returns {Boolean}
 */
export async function deletePlace(context, key) {
  context.commit('incrementPendingRequests');

  const place = context.getters.places.find(({ key: placeKey }) => placeKey === key);

  const response = await context.rootState.app.apiWorker.deletePlace({
    key,
    name: place.name,
  });

  if (!response.error) {
    context.commit('removePlace', key);
    context.rootState.app.broker.clearPlacesCache();
  }

  context.commit('decrementPendingRequests');

  return !response.error;
}

/**
 * Creates or updates a place via the Data Broker.
 *
 * @param {ActionContext} context
 * @param {Place} place
 */
export async function savePlace(context, place) {
  context.commit('incrementPendingRequests');

  const params = { data: place, name: place.name };
  let fn = 'createPlace';
  let mutation = 'insertPlace';

  if (place.key) {
    fn = 'updatePlace';
    mutation = fn;
    params.key = place.key;
  }

  const response = await context.rootState.app.broker.fetchAndTransform({
    fn,
    params,
    transformationClass: 'PlaceTransformer',
  });

  if (response && !response.error) {
    context.commit(mutation, response);
    context.dispatch('map/updatePlacesLayer', null, { root: true });
    context.rootState.app.broker.clearPlacesCache();
  }

  context.commit('decrementPendingRequests');
}

/**
 * Retrieves a list of visits for a given place via the API.
 *
 * @param {ActionContext} context
 * @param {String} key
 * @param {String} from
 * @param {String} to
 * @returns {Object[]}
 */
export async function getPlaceVisits(context, { key, from, to }) {
  context.commit('setPlaceVisits', []);

  try {
    const response = await context.rootState.app.broker.fetchAndTransform({
      fn: 'getPlaceVisits',
      params: {
        key,
        from,
        to,
      },
    });

    if (response.errors || response.error) {
      return new ErrorPayload(response.errors || response.error);
    }

    context.commit('setPlaceVisits', response.visits);
    return response.visits;
  } catch (error) {
    return new ErrorPayload(error.message);
  }
}

/**
 * Gets the best place match for a give point
 *
 * @param {ActionContext} context
 * @param {String} key
 * @param {Number} lat
 * @param {Number} lng
 * @returns {Object}
 */
export async function getPlaceAtPoint(context, { lat, lng }) {
  try {
    const place = await context.rootState.app.broker.getPlaceByCoords({
      lat,
      lng,
    });

    if (place && place.error) {
      return new ErrorPayload(place.error);
    }
    return place;
  } catch (error) {
    return new ErrorPayload(error.message);
  }
}
